import React, {ComponentType, ReactNode, useContext, useEffect, useState} from 'react';
import {ConnectedComponent} from 'react-redux';
import {Navigate} from 'react-router-dom';
import {IView} from '@src/@interfaces/views/IView';
import PageTitle from '@src/components/PageMetaTags/PageTitle';
import config from '@src/helpers/config';
import {AbstractLayoutProps} from '@src/@types/components/layouts/AbstractLayout';
import {BreadcrumbItem} from '@src/@types/components/Breadcrumbs';
import Breadcrumbs from '@src/containers/Breadcrumbs/Breadcrumbs';
import trans from '@src/helpers/trans';
import {LayoutContext} from '@src/contexts/LayoutContext';
import DefaultLayout from '@src/components/_layouts/Default/DefaultLayout';

type Layout = ComponentType<AbstractLayoutProps>;

export function PageMetaTags(props: { title: string }) {
    const {getPageMeta, onUpdate} = useContext(LayoutContext);

    type T = ReturnType<typeof getPageMeta>['pageTitlesReplacers']
    const [pageTitlesReplacers, setPageTitlesReplacers] = useState<T>(getPageMeta().pageTitlesReplacers);
    const title = trans(props.title, pageTitlesReplacers);
    const appName = config('client.app-name');

    useEffect(() => {
        const subscription = onUpdate(() => {
            setPageTitlesReplacers(getPageMeta().pageTitlesReplacers);
        });

        return () => subscription.unsubscribe();
    }, [getPageMeta, onUpdate]);

    return <PageTitle title={`${title} | ${appName}`}/>;
}

export default abstract class AbstractView<Props = any, State = any> extends React.Component<Props, State> implements IView {
    static layout: Layout | ConnectedComponent<Layout, any> = DefaultLayout;
    protected static breadcrumbsItems: BreadcrumbItem[] = [];
    protected static breadcrumbsHome?: BreadcrumbItem = undefined;

    protected static pageTitle: string;

    componentWillUnmount() {
        if (
                this.constructor &&
                ('contextType' in this.constructor) &&
                (this.constructor as typeof AbstractView).contextType === LayoutContext
        ) {
            this.context.flushPageTitleAndBreadcrumbs();
        }
    }

    render() {
        if (!this.authorize()) {
            return this.onAuthorizationFail();
        }

        return <>
            {this.renderMetaTags()}

            {this.renderContent()}
        </>;
    }

    static breadcrumbs() {
        return <Breadcrumbs
                pageTitle={this.pageTitle}
                home={this.breadcrumbsHome}
                items={this.breadcrumbsItems}
        />;
    }

    protected abstract renderContent(): ReactNode;

    protected renderMetaTags(): ReactNode {
        return <PageMetaTags title={this.getPageTitle()}/>;
    }

    protected authorize(): boolean {
        return true;
    }

    protected onAuthorizationFail(): ReactNode {
        return <Navigate to="/" replace={true}/>;
    }

    protected getPageTitle(): string {
        return (this.constructor as typeof AbstractView)?.pageTitle ?? '';
    }
}
